import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Hotspot {
  x: number;
  y: number;
  materialId: string;
}

interface MaterialInfo {
  id: string;
  name: string;
  image_url: string | null;
  category: string;
}

interface ImageHotspotBlockProps {
  content: {
    imageUrl?: string;
    hotspots?: Hotspot[];
  };
}

export function ImageHotspotBlock({ content }: ImageHotspotBlockProps) {
  const { imageUrl, hotspots = [] } = content;
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  const materialIds = hotspots.map((h) => h.materialId).filter(Boolean);

  const { data: materials } = useQuery({
    queryKey: ["hotspot-materials", materialIds],
    enabled: materialIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("id, name, image_url, category")
        .in("id", materialIds);
      if (error) throw error;
      return data as MaterialInfo[];
    },
  });

  const matMap = new Map((materials || []).map((m) => [m.id, m]));

  if (!imageUrl) return null;

  return (
    <section className="mx-auto max-w-[1200px] px-6 py-16">
      <div className="relative w-full overflow-hidden rounded-lg">
        <img
          src={imageUrl}
          alt=""
          className="w-full h-auto block"
        />
        {hotspots.map((spot, i) => {
          const mat = matMap.get(spot.materialId);
          const isActive = activeIdx === i;
          const labelOnLeft = spot.x > 65;

          return (
            <div
              key={i}
              className="absolute group/spot"
              style={{ left: `${spot.x}%`, top: `${spot.y}%`, transform: "translate(-50%, -50%)" }}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseLeave={() => setActiveIdx(null)}
              onTouchStart={(e) => {
                e.stopPropagation();
                setActiveIdx(activeIdx === i ? null : i);
              }}
            >
              {/* Pulse ring */}
              <span
                className="absolute inset-0 -m-2 rounded-full border border-white/40 animate-[hotspot-ping_2.5s_ease-out_infinite]"
                style={{ animationDelay: `${i * 0.4}s` }}
              />

              {/* Outer glow */}
              <span
                className={`absolute -inset-1.5 rounded-full bg-white/20 backdrop-blur-sm transition-all duration-300 ${
                  isActive ? "scale-[2.2] bg-white/30" : "scale-100"
                }`}
              />

              {/* Dot */}
              <span
                className={`relative z-10 block h-3 w-3 rounded-full border-2 border-white bg-primary shadow-[0_0_8px_rgba(0,0,0,0.3)] transition-all duration-300 cursor-pointer ${
                  isActive ? "scale-150 shadow-[0_0_16px_rgba(0,0,0,0.4)]" : ""
                }`}
              />

              {/* Label with thumbnail */}
              {mat && (
                <Link
                  to="/components"
                  className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-2 transition-all duration-300 ${
                    labelOnLeft ? "right-8 flex-row-reverse" : "left-8"
                  } ${
                    isActive
                      ? "opacity-100 translate-x-0"
                      : labelOnLeft
                        ? "opacity-0 translate-x-2 pointer-events-none"
                        : "opacity-0 -translate-x-2 pointer-events-none"
                  }`}
                >
                  {/* Connecting line */}
                  <span className="block h-px w-4 bg-white/60" />
                  <span className="flex items-center gap-2.5 rounded-lg bg-background/95 px-2.5 py-2 font-sans text-xs font-medium text-foreground shadow-lg backdrop-blur-md border border-border/50">
                    {mat.image_url && (
                      <img
                        src={mat.image_url}
                        alt={mat.name}
                        className="h-9 w-9 rounded object-cover flex-shrink-0"
                      />
                    )}
                    <span className="flex flex-col gap-0.5">
                      <span className="whitespace-nowrap font-medium">{mat.name}</span>
                      <span className="whitespace-nowrap text-[10px] text-muted-foreground">{mat.category}</span>
                    </span>
                  </span>
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes hotspot-ping {
          0% { transform: scale(1); opacity: 0.6; }
          70% { transform: scale(2.5); opacity: 0; }
          100% { transform: scale(2.5); opacity: 0; }
        }
      `}</style>
    </section>
  );
}
